import * as Sentry from '@sentry/react'
import {
  isCefSharpBrowserBotEvent,
  isThirdPartyInjectedFetchFailureEvent,
  isVercelLiveFeedbackEvent,
} from '@/lib/sentry-event-filters'

// Initialize Sentry BEFORE other imports to capture errors during module loading
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Release tracking - set via VITE_SENTRY_RELEASE at build time (git SHA or version tag)
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  environment: import.meta.env.MODE, // 'development', 'production', etc.
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    // Keep browser console logs in Sentry Logs.
    Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    // Mirror backend LoggingIntegration(event_level=ERROR): promote console.error
    // records to classic Sentry issue events in addition to Logs.
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],
  // Tracing
  tracesSampleRate: 1, // Capture 100% of transactions
  // Set tracePropagationTargets for distributed tracing
  tracePropagationTargets: ['localhost', /^https:\/\/.*\.supplyco\.ai\/api/, /^https:\/\/.*\.orb\.local/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% sample rate (increase in dev if needed)
  replaysOnErrorSampleRate: 1, // 100% when errors occur
  // Sentry Logs product (browser); pairs with consoleLoggingIntegration below.
  enableLogs: true,
  // Filter out benign third-party errors
  beforeSend(event) {
    // PostHog's rrweb throws SecurityError when trying to remove event listeners
    // from cross-origin iframes during session recording stop/cleanup.
    // This is benign — recording state remains consistent.
    if (
      event.exception?.values?.some(e =>
        e.type === 'SecurityError'
        && e.value?.includes('cross-origin')
        && e.stacktrace?.frames?.some(f => f.module?.includes('rrweb') || f.module?.includes('posthog')),
      )
    ) {
      return null // eslint-disable-line unicorn/no-null -- Sentry API requires null to drop events
    }

    // Vercel's live feedback instrumentation injects scripts under
    // /_next-live/feedback/ that can throw various errors we cannot fix:
    //   - TypeError from sessionStorage access in strict contexts (SUP-2350)
    //   - InvalidNodeTypeError from Range.selectNode() on detached nodes (SUP-2349)
    // These are Vercel platform bugs — our code has zero usage of these paths.
    // Match on stack frame origin rather than error type so future Vercel
    // widget errors are also suppressed.
    if (isVercelLiveFeedbackEvent(event)) {
      return null // eslint-disable-line unicorn/no-null -- Sentry API requires null to drop events
    }

    // CefSharp-based security crawlers (commonly email/link scanners) can
    // surface host-object promise rejections while preloading public pages.
    // These have no app stack and do not represent real user failures.
    if (isCefSharpBrowserBotEvent(event)) {
      return null // eslint-disable-line unicorn/no-null -- Sentry API requires null to drop events
    }

    // Browser extensions and injected third-party scripts can create failed
    // fetch promise rejections on our pages. Drop only events with an external
    // failed-fetch breadcrumb and no first-party app frame.
    if (isThirdPartyInjectedFetchFailureEvent(event)) {
      return null // eslint-disable-line unicorn/no-null -- Sentry API requires null to drop events
    }

    return event
  },
})

// eslint-disable-next-line import/first -- Sentry must init before other imports
import { StrictMode } from 'react'
// eslint-disable-next-line import/first
import ReactDOM from 'react-dom/client'
// eslint-disable-next-line import/first
import { client } from '@/client/client.gen'
// eslint-disable-next-line import/first
import { getActingUserId } from '@/lib/acting-user'
// eslint-disable-next-line import/first
import { openSessionChangedModal, SESSION_MISMATCH_CODE } from '@/lib/session-changed-store'
// eslint-disable-next-line import/first
import { expireActiveSession } from '@/lib/session-expiry'
// eslint-disable-next-line import/first
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
// eslint-disable-next-line import/first
import App from './app'
// eslint-disable-next-line import/first
import './globals.css'

client.setConfig({
  baseUrl: import.meta.env.VITE_FASTAPI_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
  credentials: 'include', // This ensures cookies are sent with requests
})

// Skip retry on 412 globally for queries. A 412 response from this backend
// means a precondition failed (today: session-mismatch — see SUP-3373); the
// same request with the same headers and same cookie will get the same 412
// on retry. Default TanStack query retry (3 attempts) just hammers the
// backend with identical failing requests and re-triggers the modal.
// Mutations are NOT overridden — TanStack's default is 0 retries, which is
// what we want (a failed POST/PATCH should surface its onError immediately,
// not silently retry 3× and delay the toast/UI). The response interceptor
// already stalls 412 mutations before they reach the caller, so the
// retry-on-412 carve-out is redundant for mutations.
const DEFAULT_RETRY_COUNT = 3
const PRECONDITION_FAILED_STATUS = 412
function retryUnlessPreconditionFailed(failureCount: number, error: unknown): boolean {
  if ((error as { status?: number })?.status === PRECONDITION_FAILED_STATUS) {
    return false
  }
  return failureCount < DEFAULT_RETRY_COUNT
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: retryUnlessPreconditionFailed },
  },
})

// Declare which user this tab's React state believes it is acting as on
// every outgoing request. Backend compares against the cookie-resolved
// user and returns 412 if they disagree (SUP-3373). Header is omitted
// when no user is known (initial load, post-logout) so the backend's
// "no claim, no check" path keeps working for login/anonymous routes.
client.interceptors.request.use((request) => {
  const id = getActingUserId()
  if (id) {
    request.headers.set('X-Acting-User-Id', id)
  }
  return request
})

// Redirect to login when session becomes invalid (401 on an active session).
// Login-attempt 401s are excluded because the session flag is only set after successful login.
// Uses location.assign (not router.navigate) because the interceptor can fire before
// RouterProvider mounts, and the login route's beforeLoad accesses router context.
client.interceptors.response.use((response) => {
  if (response.status === 401)
    expireActiveSession({ clearClientState: () => queryClient.clear() })
  return response
})

// Surface session-mismatch (cookie swapped by sibling tab) via a blocking
// modal AND stall the response so the SDK's "throw on error" never fires
// downstream — otherwise the rejected promise routes through every
// caller's onError, triggering destructive toasts that pipe through
// console.error and pollute Sentry with what is expected behavior. The
// modal is the only UX needed; the page reloads from there, GC'ing the
// hanging request. Gated on BOTH status === 412 AND
// detail.code === SESSION_MISMATCH_CODE so a future 412 for another
// reason (e.g. ETag preconditions) flows normally to its caller.
// SUP-3373.
client.interceptors.response.use(async (response) => {
  if (response.status !== 412) {
    return response
  }
  let body: { detail?: { code?: string } } | undefined
  try {
    body = await response.clone().json() as { detail?: { code?: string } } | undefined
  }
  catch {
    // Non-JSON body: not a session_mismatch we recognize. Fall through.
  }
  if (body?.detail?.code !== SESSION_MISMATCH_CODE) {
    return response
  }
  openSessionChangedModal()
  // Stall: never resolve, never reject. The modal blocks the UI, the
  // user clicks Reload, and the entire JS context is replaced. Until
  // then, every caller's mutation/query stays "pending" — which is
  // invisible because the modal covers the UI.
  return new Promise<Response>(() => {
    /* intentionally never resolves */
  })
})

// Suppress benign unhandled rejections from navigation-aborted fetches.
// When React Query cancels in-flight requests via AbortSignal during route transitions,
// the rejection can escape React Query's error handling in edge cases.
globalThis.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  if (
    reason === undefined
    || reason?.name === 'AbortError'
    || (reason instanceof DOMException && reason.code === DOMException.ABORT_ERR)
  ) {
    event.preventDefault()
  }
})

const rootElement = document.querySelector('#root')!

if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  )
}
